import { useState } from 'react';

import { Glow } from '@/components/ui';
import { cn } from '@/utils/cn';

import { CAP, LETTERS, VIEWBOX, WORDMARK_NODES, scatterFor } from './wordmarkGeometry';
import {
  HEADLINE_FROM,
  HEADLINE_TO,
  useHeadingZoomOut,
  useScrollZoom,
  useSectionReveal,
} from './useSectionReveal';

/**
 * Placeholder listings, in the spirit of the design's own invented ones.
 *
 * None of these companies exist and none of these figures are real -- they
 * illustrate what a match looks like. Swap them for `internshipApi` results
 * before launch, or the page is showing openings nobody can apply to.
 */
const CARDS = [
  {
    company: 'ORBIT',
    role: 'ML Engineering Intern',
    place: 'London',
    pay: '$1.800/mo',
    tags: ['Python', 'LLMs'],
  },
  {
    company: 'VELA',
    role: 'Brand Intern',
    place: 'Paris',
    pay: '$1.100/mo',
    tags: ['Identity', 'Copy'],
  },
  {
    company: 'NOVA',
    role: 'Product Design Intern',
    place: 'Berlin',
    pay: '$1.400/mo',
    tags: ['Figma', 'Research'],
  },
  {
    company: 'ATLAS',
    role: 'Data Analyst Intern',
    place: 'Amsterdam',
    pay: '$1.300/mo',
    tags: ['SQL', 'dbt'],
  },
  {
    company: 'HELIX',
    role: 'Growth Intern',
    place: 'Lisbon',
    pay: '$1.000/mo',
    tags: ['SEO', 'Lifecycle'],
  },
  {
    company: 'LUMEN',
    role: 'Frontend Intern',
    place: 'Dublin',
    pay: '$1.500/mo',
    tags: ['React', 'TypeScript'],
  },
];

/** Radius of the lit point. */
const DOT_RADIUS = 6.5;

/**
 * What the wide, blurred copy of each stroke settles at.
 *
 * Named because the assembly tween has to put it back: the two copies of a
 * stroke finish on different opacities, so there is no one value to animate
 * to and each path carries its own on the element.
 */
const BLOOM_OPACITY = 0.35;

/**
 * A dot's own pulse rhythm.
 *
 * Every ring on one clock reads as the whole word breathing, which is a
 * different effect entirely. These periods are deliberately incommensurate
 * and the delay is negative, so each point is already somewhere else in its
 * cycle on the first frame and the field never resynchronises.
 *
 * Derived from the index rather than `Math.random`, so a dot keeps its rhythm
 * across re-renders instead of being reseeded every time the hovered card
 * changes.
 */
function pulseFor(index) {
  const seed = ((index * 2654435761) % 1000) / 1000;
  return {
    animationDuration: `${(2.6 + seed * 2.4).toFixed(2)}s`,
    animationDelay: `-${(seed * 6).toFixed(2)}s`,
  };
}

/** Keeps a card from hanging off the edge when its node sits near one. */
const EDGE = 15;

function positionFor(node) {
  const left = ((node.x - VIEWBOX.x) / VIEWBOX.width) * 100;
  const top = ((node.y - VIEWBOX.y) / VIEWBOX.height) * 100;
  return { left: Math.min(Math.max(left, EDGE), 100 - EDGE), top };
}

/**
 * "A curated universe, not a job board."
 *
 * WONDER is drawn as a constellation -- open strokes with a lit point at every
 * end -- and each point carries an opening. The claim the section makes is
 * that the word is made of matches, so the openings have to live *in* the
 * letters rather than in a row beneath them.
 *
 * The SVG is `aria-hidden`. Forty focusable points carrying placeholder
 * listings would bury a keyboard user in tab stops for content that is
 * illustrative and reachable in full on the internships page. The heading and
 * copy carry the section's meaning; once these are real listings, the honest
 * fix is a visible list beside the constellation rather than making the dots
 * focusable.
 */
export function CuratedUniverse() {
  const [active, setActive] = useState(null);
  const zoom = useScrollZoom();
  const heading = useHeadingZoomOut();

  const root = useSectionReveal(({ timeline, reveal, select }) => {
    reveal('.js-cu-line', HEADLINE_FROM, HEADLINE_TO);
    reveal('.js-cu-sub', { y: 24, opacity: 0 }, { duration: 0.7, ease: 'power3.out' }, 0.3);
    // The word assembles itself. Every stroke starts thrown clear of where it
    // belongs, visible the whole way, and flies home -- the section's claim is
    // that the word is made of matches, so the letters have to be seen
    // arriving rather than switched on in place.
    //
    // `from: 'random'` is doing real work. Ordered, the strokes arrive left to
    // right and the eye follows the leading edge, which reads as a wipe
    // crossing the word; scattered, there is no edge to follow and the whole
    // thing is seen closing in at once.
    //
    // Each path carries its own start offset and its own finish opacity as
    // attributes: both copies of a stroke travel together, but the bloom
    // settles at 0.35 and the bright line at 1.
    timeline.fromTo(
      select('.js-cu-stroke'),
      {
        x: (index, element) => Number(element.dataset.fx),
        y: (index, element) => Number(element.dataset.fy),
        opacity: 0,
      },
      {
        x: 0,
        y: 0,
        opacity: (index, element) => Number(element.dataset.o),
        duration: 1.25,
        ease: 'power3.out',
        stagger: { each: 0.03, from: 'random' },
      },
      0.45,
    );
    // Nothing here animates a transform, and that is the point. Scaling an
    // SVG group makes GSAP bake an origin offset into the `transform`
    // attribute and it does not resolve back to identity when the tween ends
    // -- every dot was left translated by exactly its own hit radius, sitting
    // up and left of the stroke end it belongs to. Fading the group and
    // growing the dot's radius gives the same pop and cannot drift.
    reveal(
      '.js-cu-node',
      { opacity: 0 },
      { duration: 0.4, stagger: 0.015, ease: 'power2.out' },
      2.35,
    );
    timeline.fromTo(
      select('.js-cu-dot'),
      { attr: { r: 0 } },
      { attr: { r: DOT_RADIUS }, duration: 0.45, stagger: 0.015, ease: 'back.out(2)' },
      2.35,
    );
  });

  const activeNode = active === null ? null : WORDMARK_NODES[active];
  const activeCard = active === null ? null : CARDS[active % CARDS.length];
  // A point low in the word would push its card past the bottom of the
  // section, so those hang it above themselves instead.
  const cardAbove = activeNode !== null && activeNode.y > CAP * 0.55;

  return (
    // Clipped sideways only, like every other section on the page. Both glows
    // below hang off the edge -- that is what makes them read as light
    // spilling in rather than as discs -- and unclipped the right-hand one
    // pushed the document wider than the viewport. Top and bottom stay open,
    // with no ground of its own, so the light fades on into the neighbouring
    // sections instead of stopping at a seam.
    <section ref={root} className="wonder-screen relative overflow-x-clip py-20 lg:py-28">
      <div>
        <Glow className="top-1/3 -left-40" size={680} opacity={0.14} />
        <Glow className="-right-32 bottom-0" size={560} tone="lilac" opacity={0.1} />

        <div ref={zoom} className="wonder-container wonder-measure relative">
          {/* Heading and subline zoom away together as the section leaves,
              so the ref sits on the block that holds both. */}
          <div ref={heading}>
            <h2 className="mx-auto max-w-4xl text-center text-[clamp(2rem,3.15vw,3.75rem)] leading-[1.1] font-medium tracking-[-0.033em]">
              <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
                <span className="js-cu-line text-on-night block">A curated universe,</span>
              </span>{' '}
              <span className="block overflow-hidden pb-[0.1em] mb-[-0.1em]">
                <span className="js-cu-line text-iris-300 block">not a job board.</span>
              </span>
            </h2>

            <p className="js-cu-sub text-[#FFFFFFCC] mx-auto mt-8 max-w-208 text-center text-[clamp(1rem,1.05vw,1.25rem)] leading-normal">
              Wonder compares skills, ambition, availability and startup needs to surface the best
              internship opportunities automatically.
            </p>
          </div>

          {/* Bottom padding leaves room for a card raised by one of the lower
            points, which is meant to overlap the letters rather than push
            them around. */}
          <div className="relative mt-8 pb-24 lg:mt-10">
            {/*
            Capped short of the container rather than filling it. In the design
            the word takes a little over four fifths of the column, which is
            what keeps it reading as a mark set inside the section instead of a
            banner stretched across it, and it holds that proportion at every
            width because the cap is a percentage rather than a size.
          */}
            <svg
              aria-hidden="true"
              className="mx-auto w-full max-w-[82%] overflow-visible"
              viewBox={`${VIEWBOX.x} ${VIEWBOX.y} ${VIEWBOX.width} ${VIEWBOX.height}`}
              fill="none"
            >
              <defs>
                <filter id="cu-bloom" x="-20%" y="-40%" width="140%" height="180%">
                  <feGaussianBlur stdDeviation="6" />
                </filter>
                {/* The dots get their own filter with a square region wide
                    enough for the whole blur. The stroke filter's region is
                    cut to the shape of a line -- narrow across, tall down --
                    and on a circle that clipped the halo's sides off and
                    left it an upright oval. */}
                <filter id="cu-halo" x="-150%" y="-150%" width="400%" height="400%">
                  <feGaussianBlur stdDeviation="4" />
                </filter>
              </defs>

              {LETTERS.map((letter) => (
                <g key={letter.id} transform={`translate(${letter.x} 0)`}>
                  {/* Each stroke twice: wide and blurred for the bloom, then thin
                    and bright over it. One stroke wearing a drop-shadow gives a
                    hard edge with a halo; two read as light.

                    Both copies take the same `scatterFor` offset, so a line and
                    its halo fly in together instead of arriving separately and
                    pulling apart on the way. */}
                  {letter.strokes.map((d, strokeIndex) => {
                    const from = scatterFor(letter, strokeIndex);
                    return (
                      <path
                        key={`bloom-${d}`}
                        className="js-cu-stroke"
                        data-fx={from.x}
                        data-fy={from.y}
                        data-o={BLOOM_OPACITY}
                        d={d}
                        stroke="#A88CF5"
                        strokeWidth="8"
                        strokeLinecap="round"
                        opacity={BLOOM_OPACITY}
                        filter="url(#cu-bloom)"
                      />
                    );
                  })}
                  {letter.strokes.map((d, strokeIndex) => {
                    const from = scatterFor(letter, strokeIndex);
                    return (
                      <path
                        key={`line-${d}`}
                        className="js-cu-stroke"
                        data-fx={from.x}
                        data-fy={from.y}
                        data-o={1}
                        d={d}
                        stroke="#C4B0FA"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    );
                  })}
                </g>
              ))}

              {WORDMARK_NODES.map((node, index) => (
                <g
                  key={node.id}
                  className="js-cu-node cursor-pointer"
                  onPointerEnter={() => setActive(index)}
                  onPointerLeave={() =>
                    setActive((current) => (current === index ? null : current))
                  }
                  // Touch has no hover, so a tap does the same job.
                  onClick={() => setActive((current) => (current === index ? null : index))}
                >
                  {/* The halo is a soft disc not much wider than the dot: a
                    hint of light around the point rather than a pool under
                    it. */}
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r="10"
                    fill="#C4B0FA"
                    opacity="0.4"
                    filter="url(#cu-halo)"
                  />
                  {/* The pulse is CSS rather than another GSAP tween: it runs
                    for the life of the page on thirty elements, and as a
                    compositor-driven keyframe it costs nothing per frame. It
                    drives the ring's own transform and opacity while the
                    reveal above drives the group's, so the two multiply
                    instead of fighting. */}
                  <circle
                    className="wonder-node-pulse"
                    style={pulseFor(index)}
                    cx={node.x}
                    cy={node.y}
                    r={DOT_RADIUS + 1}
                    fill="none"
                    stroke="#E8DDFF"
                    strokeWidth="2.5"
                  />
                  <circle
                    className="js-cu-dot"
                    cx={node.x}
                    cy={node.y}
                    r={DOT_RADIUS}
                    fill="#FFFFFF"
                  />
                  {/* A dot this small is a hard thing to hit; the pointer target
                    is nearly three times its radius and invisible. */}
                  <circle cx={node.x} cy={node.y} r="18" fill="transparent" />
                </g>
              ))}
            </svg>

            {activeNode && (
              <article
                className={cn(
                  'border-night-line bg-night-deep/85 pointer-events-none absolute z-10',
                  'rounded-2xl border p-5 shadow-xl backdrop-blur-md',
                  'w-56',
                  cardAbove ? 'wonder-card-up' : 'wonder-card-in',
                )}
                style={{
                  left: `${positionFor(activeNode).left}%`,
                  top: `calc(${positionFor(activeNode).top}% ${cardAbove ? '-' : '+'} 1.25rem)`,
                }}
              >
                <p className="text-on-night-subtle text-[0.7rem] font-medium tracking-[0.2em] uppercase">
                  {activeCard.company}
                </p>
                <p className="text-on-night mt-3 font-semibold">{activeCard.role}</p>
                <p className="text-on-night-muted mt-1 text-sm">
                  {activeCard.place} · {activeCard.pay}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {activeCard.tags.map((tag) => (
                    <span
                      key={tag}
                      className="border-night-line text-on-night-muted rounded-full border bg-white/5 px-3 py-1 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
